import {
  Container,
  Typography,
  Card,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  FormGroup,
  Grid,
  TextField,
  Autocomplete,
  styled,
  Alert,
  Checkbox,
  Box,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  InputAdornment,
  Snackbar,
  Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Page from '../components/Page';
import { getAnalyticsMain, getAnalyticsSub, getCamerasList, getSavedAnalytics, setAnalytics, getClientList } from 'src/api/analytics';
import Iconify from 'src/components/Iconify';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState, useMemo } from 'react';
import { createFilterOptions } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getClientProperties } from 'src/api/ShiftMaster';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// ─── Styled Components ────────────────────────────────────────────────────────

const ModelCard = styled(Card)(({ theme, active }) => ({
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  boxShadow: 'none',
  cursor: 'pointer',
  transition: 'border-color 0.2s, background 0.2s',
  background: active
    ? alpha(theme.palette.primary.main, 0.08)
    : theme.palette.background.paper,
  '&:hover': {
    borderColor: active ? theme.palette.primary.light : alpha(theme.palette.primary.main, 0.5),
  },
}));

const IconBox = styled(Box)(({ theme, active }) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: active
    ? alpha(theme.palette.primary.main, 0.2)
    : alpha(theme.palette.grey[500], 0.1),
  color: active ? theme.palette.primary.light : theme.palette.text.secondary,
  flexShrink: 0,
}));

const FilterSection = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  padding: theme.spacing(2, 2.5),
  marginBottom: theme.spacing(2.5),
}));

const SubRow = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.2, 1.5),
  borderRadius: 8,
  marginBottom: theme.spacing(0.5),
  background: active
    ? alpha(theme.palette.primary.main, 0.08)
    : alpha(theme.palette.grey[500], 0.04),
  border: `1px solid ${active ? alpha(theme.palette.primary.main, 0.3) : 'transparent'}`,
  transition: 'background 0.15s, border-color 0.15s',
  '&:last-child': { marginBottom: 0 },
}));

// ─── Data Helpers ─────────────────────────────────────────────────────────────

const manageAnalyticsMain = (response) => {
  const results = {};
  response.forEach((row) => {
    row.isActive = false;
    results[row.Id] = row;
  });
  return results;
};

const manageAnalyticSub = (response) => {
  const results = {};
  response.forEach((row) => {
    row.isActive = false;
    if (!(row.AnalyticId in results)) results[row.AnalyticId] = [];
    results[row.AnalyticId].push(row);
  });
  return results;
};

const manageAnalyticsSettings = (response) => {
  const results = {};
  if (response) {
    response.forEach((row) => {
      if (!(row.CameraId in results))
        results[row.CameraId] = { mainIds: [], subIds: [] };
      results[row.CameraId].mainIds.push(row.analyticsmainid);
      results[row.CameraId].subIds.push(row.analyticssubid);
    });
  }
  return results;
};

const normalizeDisplayName = (name) => String(name || '').trim().toLowerCase();

const buildUniqueClientOptions = (clients) => {
  const grouped = new Map();
  (clients || []).forEach((clientItem) => {
    const key = normalizeDisplayName(clientItem?.displayname);
    if (!key) return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(clientItem);
  });
  return Array.from(grouped.values()).map((groupItems) => {
    if (groupItems.length === 1) return groupItems[0];
    const assignTargets = new Set(
      groupItems.map((item) => Number(item.AssignClient || 0)).filter((id) => id > 0)
    );
    const mappedMain = groupItems.find((item) => assignTargets.has(Number(item.ID)));
    if (mappedMain) return mappedMain;
    return groupItems.find((item) => Number(item.AssignClient || 0) === 0) || groupItems[0];
  });
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function Analytics() {
  const theme = useTheme();

  const [cameras, setCameras] = useState([]);
  const [activeCameras, setActiveCameras] = useState([]);
  const [analyticsMain, setAnalyticsMain] = useState({});
  const [analyticSub, setAnalyticSub] = useState({});
  const [activeAnalyticSub, setActiveAnalyticSub] = useState({ isOpen: false, activeId: [], title: null, mainId: null });
  const [activeSubIDs, setActiveSubIDs] = useState([]);
  const [activeMainIDs, setActiveMainIDs] = useState([]);
  const [isSaving, setSaveLoadStatus] = useState(false);
  const [settingAlert, setSettingAlert] = useState(false);
  const [analyticsSettings, setAnalyticsSettings] = useState({});
  const [savedResponse, setSavedResponse] = useState({ status: false, title: null, isError: false });
  const [client, setClient] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientCameras, setSelectedClientCameras] = useState([]);
  const [inputValue, setInputValue] = useState([]);
  const [isClient, setIsClient] = useState(false);
  const [openAuto, setOpenAuto] = useState(false);
  const [clientLocationOptions, setClientLocationOptions] = useState([]);
  const [selectedClientLocations, setSelectedClientLocations] = useState([]);
  const [locationCheckValue, setLocationCheckValue] = useState(false);
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [checkValue, setCheckValue] = useState(false);
  const [cardSearch, setCardSearch] = useState('');
  const savedAnalyticsClientId = selectedClientLocations.length > 0
    ? selectedClientLocations.join(',')
    : (selectedClients[0] || '');

  // loading states
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingClients, setLoadingClients] = useState(true);

  // ── Init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const userType = localStorage.getItem('user_type');
    if (userType === '2') {
      setIsClient(true);
      const id = localStorage.getItem('ID');
      getSavedAnalytics(id, (res) => {
        if (res.status === 200) setAnalyticsSettings({ ...manageAnalyticsSettings(res.data) });
      });
    }

    getCamerasList((res) => { if (res.status === 200) setCameras(res.data); });

    getClientList((res) => {
      if (res.status === 200) {
        const data = res.data.filter(val=>val.EnvironmentType!==0);
        setClient([...data].sort((a, b) => a.displayname.trim().localeCompare(b.displayname.trim())));
      }
      setLoadingClients(false);
    });

    let mainDone = false;
    let subDone = false;
    const checkDone = () => { if (mainDone && subDone) setLoadingCards(false); };

    getAnalyticsMain((res) => {
      if (res.status === 200) setAnalyticsMain({ ...manageAnalyticsMain(res.data) });
      mainDone = true; checkDone();
    });

    getAnalyticsSub((res) => {
      if (res.status === 200) setAnalyticSub({ ...manageAnalyticSub(res.data) });
      subDone = true; checkDone();
    });
  }, []);

  useEffect(() => {
    if (isClient) setSelectedClientCameras(cameras);
  }, [cameras]);

  useEffect(() => {
    if (localStorage.getItem('user_type') !== '2') {
      getSavedAnalytics(savedAnalyticsClientId, (res) => {
        if (res.status === 200) setAnalyticsSettings({ ...manageAnalyticsSettings(res.data) });
      });
    }
  }, [selectedClients, selectedClientLocations, savedAnalyticsClientId]);

  useEffect(() => {
    if (settingAlert) setTimeout(() => setSettingAlert(false), 2000);
  }, [settingAlert]);

  useEffect(() => {
    const availableIds = new Set((selectedClientCameras || []).map((c) => c.deviceid));
    setInputValue((prev) => prev.filter((c) => availableIds.has(c.deviceid)));
  }, [selectedClientCameras.length]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCameras = (_, value) => {
    if (value.length > 1 && value.every((e, _i, arr) => JSON.stringify(e) === JSON.stringify(arr[0]))) {
      value = [];
    }
    let actIDs = [...activeSubIDs];
    let activeMains = [...activeMainIDs];
    const containsAll = value.some((obj) => obj.hasOwnProperty('all'));

    if (value.length === 0 || !containsAll) {
      actIDs = []; activeMains = []; setCheckValue(false);
    }

    let actCameras = [];
    if (value.length > 0 && !containsAll) {
      value.forEach((row) => {
        actCameras.push(row.deviceid);
        if (row.deviceid in analyticsSettings) {
          actIDs = [...actIDs, ...analyticsSettings[row.deviceid].subIds];
          activeMains = [...activeMains, ...analyticsSettings[row.deviceid].mainIds];
        }
      });
      if (selectedClientCameras.length === actCameras.length) setCheckValue(true);
      setActiveSubIDs([...new Set(actIDs)]);
      setActiveMainIDs([...new Set(activeMains)]);
      setInputValue([...value]);
      setActiveCameras([...actCameras]);
    }

    if (value.length > 0 && containsAll) {
      let list = selectedClientCameras;
      actCameras = []; actIDs = [...activeSubIDs]; activeMains = [...activeMainIDs];

      if (activeCameras.length === selectedClientCameras.length) {
        setCheckValue(false);
        const deviceIds = value.filter((o) => !o.hasOwnProperty('all')).map((o) => o.deviceid);
        list = list.filter((item) => !deviceIds.includes(item.deviceid));
        list.forEach((row) => {
          actCameras.push(row.deviceid);
          if (row.deviceid in analyticsSettings) {
            actIDs = [...actIDs, ...analyticsSettings[row.deviceid].subIds];
            activeMains = [...activeMains, ...analyticsSettings[row.deviceid].mainIds];
          }
        });
        setActiveSubIDs([...new Set(actIDs)]);
        setActiveMainIDs([...new Set(activeMains)]);
        setInputValue(selectedClientCameras);
        setActiveCameras([...actCameras]);
        return;
      }
      if (!checkValue) {
        list.forEach((row) => {
          actCameras.push(row.deviceid);
          if (row.deviceid in analyticsSettings) {
            actIDs = [...actIDs, ...analyticsSettings[row.deviceid].subIds];
            activeMains = [...activeMains, ...analyticsSettings[row.deviceid].mainIds];
          }
        });
        setActiveSubIDs([...new Set(actIDs)]);
        setActiveMainIDs([...new Set(activeMains)]);
        setInputValue(selectedClientCameras);
        setActiveCameras([...actCameras]);
        setCheckValue(true);
      } else {
        setActiveSubIDs([]); setActiveMainIDs([]); setInputValue([]); setActiveCameras([]);
      }
    } else if (value.length === 0) {
      setActiveSubIDs([]); setActiveMainIDs([]); setInputValue([]); setActiveCameras([]); setCheckValue(false);
    }
  };

  const handleMainAnalytics = (id, displayName) => {
    setActiveAnalyticSub({ isOpen: true, activeId: analyticSub[id], title: displayName, mainId: id });
  };

  const handleClose = () => {
    setActiveAnalyticSub({ isOpen: false, activeId: [], title: null, mainId: null });
  };

  const handleSwitchBar = (row) => {
    if (row && activeCameras.length > 0) {
      const actIDs = [...activeSubIDs];
      if (!actIDs.includes(row.Id)) actIDs.push(row.Id);
      else actIDs.splice(actIDs.indexOf(row.Id), 1);
      setActiveSubIDs([...actIDs]);

      let isAdd = false;
      activeAnalyticSub.activeId.forEach((item) => { if (actIDs.includes(item.Id)) isAdd = true; });

      const activeMains = [...activeMainIDs];
      if (isAdd && !activeMains.includes(row.AnalyticId)) {
        activeMains.push(row.AnalyticId); setActiveMainIDs([...activeMains]);
      } else if (!isAdd && activeMains.includes(row.AnalyticId)) {
        activeMains.splice(activeMains.indexOf(row.AnalyticId), 1); setActiveMainIDs([...activeMains]);
      }
      setSettingAlert(false);
    } else {
      setSettingAlert(true);
    }
  };

  const showResponse = (title, isError = false) => {
    setSavedResponse({ status: true, title, isError });
    setTimeout(() => setSavedResponse({ status: false, title: null, isError: false }), 3000);
  };

  const resetData = () => {
    setActiveSubIDs([]);
    setActiveMainIDs([]);
    setActiveCameras([]);
    setInputValue([]);
    setCheckValue(false);
  };

  const saveData = () => {
    if (activeCameras.length > 0) {
      setSaveLoadStatus(true);
      setAnalytics((res) => {
        if (res.status === 200 || res.status === 201) {
          showResponse('Setup saved successfully!');
          handleReload();
        }
      }, { CameraId: activeCameras.toString(), AnalyticsSubId: activeSubIDs.toString() });
    }
  };

  const handleReload = () => {
    getSavedAnalytics(savedAnalyticsClientId, (res) => {
      if (res.status === 200) {
           setSaveLoadStatus(false);
           setAnalyticsSettings({ ...manageAnalyticsSettings(res.data) });
      }
        
    });
  };

  const handleClient = (_, value) => {
    setCheckValue(false); setInputValue([]); setActiveCameras([]);
    setActiveSubIDs([]); setActiveMainIDs([]);
    setClientLocationOptions([]); setSelectedClientLocations([]);
    setLocationCheckValue(false); setSelectedClientOption(value || null);

    if (!value) { setSelectedClientCameras([]); setSelectedClients([]); return; }

    const selectedClientId = Number(value.ID);
    setSelectedClients([selectedClientId]);

    getClientProperties({ AssignClient: String(selectedClientId) }, (res) => {
      if (res.status === 200) {
        const normalizedLocations = Array.from(
          new Map(
            (Array.isArray(res.data) ? res.data : res.data ? [res.data] : [])
              .map((loc) => ({ ...loc, ID: Number(loc?.ID ?? loc?.id ?? 0), Location: String(loc?.Location || loc?.name || '').trim() }))
              .filter((loc) => loc.ID > 0)
              .map((loc) => [loc.ID, loc])
          ).values()
        );
        setClientLocationOptions(normalizedLocations);
        if (normalizedLocations.length > 0) { setSelectedClientCameras([]); return; }
      }
      setSelectedClientCameras((cameras || []).filter((c) => Number(c.clientid) === selectedClientId));
    });
  };

  const handleLocationFilterOptions = (options, params) => {
    const filter = createFilterOptions();
    const filtered = filter(options, params);
    return options.length > 0 ? [{ title: 'Select All', selectall: true }, ...filtered] : filtered;
  };

  const handleLocationChange = (_, value) => {
    let next = value.some((o) => o.hasOwnProperty('selectall'))
      ? (locationCheckValue ? [] : clientLocationOptions.map((l) => l.ID || l.id))
      : value.map((l) => l.ID || l.id);

    setSelectedClientLocations(next);
    setLocationCheckValue(clientLocationOptions.length > 0 && next.length === clientLocationOptions.length);
    setCheckValue(false); setInputValue([]); setActiveCameras([]); setActiveSubIDs([]); setActiveMainIDs([]);

    if (next.length === 0) { setSelectedClientCameras([]); return; }
    const idSet = new Set(next.map(String));
    setSelectedClientCameras(
      (cameras || []).filter((item) => {
        const cid = String(item?.clientid ?? item?.client_id ?? item?.ClientId ?? '');
        return cid !== '' && idSet.has(cid);
      })
    );
  };

  const handleFilterOptions = (options, params) => {
    const filter = createFilterOptions();
    const filtered = filter(options, params);
    return [{ title: 'Select All', all: true }, ...filtered];
  };

  // ── Memos ─────────────────────────────────────────────────────────────────

  const uniqueClientOptions = useMemo(() => buildUniqueClientOptions(client || []), [client]);

  const selectedLocationOptions = useMemo(
    () => clientLocationOptions.filter((loc) => selectedClientLocations.includes(loc.ID || loc.id)),
    [clientLocationOptions, selectedClientLocations]
  );

  const filteredAnalyticsMain = useMemo(() => {
    const all = Object.values(analyticsMain);
    if (!cardSearch.trim()) return all;
    const q = cardSearch.toLowerCase();
    return all.filter((row) => (row.DisplayName || '').toLowerCase().includes(q));
  }, [analyticsMain, cardSearch]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Page title="Analytics">
      <Container maxWidth="xxl" disableGutters>

        {/* Page header */}
        <Box sx={{ mb: 3, pt: 1, px: 2.5 }}>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Analytics</Typography>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/" sx={{ fontSize: '0.8rem' }}>Dashboard</Link>
            <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>Settings</Typography>
            <Typography color="text.primary" sx={{ fontSize: '0.8rem' }}>Analytics</Typography>
          </Breadcrumbs>
        </Box>

        {/* Filter section */}
        <Box sx={{ px: 2.5 }}>
          <FilterSection>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Filters
            </Typography>
            <Grid container spacing={2}>
              {!isClient && (
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    id="client-select"
                    options={uniqueClientOptions.map((c, _i) => ({ ...c, customKey: `${c.ID || c.id}-${_i}` }))}
                    isOptionEqualToValue={(o, v) => o.ID === v.ID}
                    value={selectedClientOption}
                    getOptionLabel={(o) => o.displayname || ''}
                    onChange={handleClient}
                    loading={loadingClients}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        placeholder="Search client..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <Iconify icon="mdi:account-outline" width={18} height={18} color={theme.palette.text.secondary} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.customKey}>{option.displayname}</li>
                    )}
                  />
                </Grid>
              )}

              {!isClient && selectedClientOption && clientLocationOptions.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    multiple
                    id="location-select"
                    options={clientLocationOptions}
                    value={selectedLocationOptions}
                    getOptionLabel={(o) => o.selectall ? o.title : (o.Location || '')}
                    onChange={handleLocationChange}
                    disableCloseOnSelect
                    filterOptions={handleLocationFilterOptions}
                    renderTags={() => null}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }}
                          checked={option.selectall ? locationCheckValue : selectedClientLocations.includes(option.ID || option.id)}
                        />
                        {option.selectall ? option.title : option.Location}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Locations"
                        placeholder={selectedClientLocations.length > 0 ? `${selectedClientLocations.length} selected` : 'Search...'}
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <Iconify icon="mdi:map-marker-outline" width={18} height={18} color={theme.palette.text.secondary} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  id="camera-select"
                  options={selectedClientCameras}
                  getOptionLabel={(o) =>
                    o.all && selectedClientCameras.length === activeCameras.length
                      ? 'Select All'
                      : `${o.deviceid} ${o.devicename}`
                  }
                  onChange={handleCameras}
                  value={checkValue ? [{ title: 'Select All', all: true }] : inputValue.filter((c) => activeCameras.includes(c.deviceid))}
                  disableCloseOnSelect={openAuto}
                  open={openAuto}
                  onOpen={() => setOpenAuto(true)}
                  onClose={() => setOpenAuto(false)}
                  filterOptions={handleFilterOptions}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.all ? (
                        <>
                          <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }} checked={checkValue} onChange={() => setCheckValue(!checkValue)} />
                          {option.title}
                        </>
                      ) : (
                        <>
                          <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }} checked={activeCameras.includes(option.deviceid)} />
                          {option.devicename}
                        </>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cameras"
                      placeholder={activeCameras.length > 0 ? `${activeCameras.length} selected` : 'Search camera...'}
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Iconify icon="mdi:camera-outline" width={18} height={18} color={theme.palette.text.secondary} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={() => null}
                />
              </Grid>
            </Grid>
          </FilterSection>
        </Box>

        {/* Cards section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>Analytics Models</Typography>
            {!loadingCards && Object.values(analyticsMain).length > 0 && (
              <Chip
                label={`${activeMainIDs.length} / ${Object.values(analyticsMain).length} active`}
                size="small"
                color={activeMainIDs.length > 0 ? 'primary' : 'default'}
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
              />
            )}
          </Box>
          <TextField
            size="small"
            placeholder="Search models..."
            value={cardSearch}
            onChange={(e) => setCardSearch(e.target.value)}
            sx={{ width: 210 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={18} height={18} color={theme.palette.text.disabled} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Analytics model cards */}
        <Box sx={{ px: 2.5 }}>
          <Grid container spacing={1.5} sx={{ mb: 1 }}>
            {loadingCards
              ? Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} lg={2} key={i}>
                  <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, background: theme.palette.background.paper }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 1.5 }} />
                      <Skeleton variant="rounded" width={30} height={18} sx={{ borderRadius: 1 }} />
                    </Box>
                    <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="50%" height={16} />
                  </Box>
                </Grid>
              ))
              : filteredAnalyticsMain.map((row, key) => {
                const isActive = activeMainIDs.includes(row.Id);
                const subCount = (analyticSub[row.Id] || []).length;
                const activeSubCount = (analyticSub[row.Id] || []).filter((s) => activeSubIDs.includes(s.Id)).length;
                return (
                  <Grid item xs={12} sm={6} md={3} lg={2} key={key}>
                    <ModelCard active={isActive ? 1 : 0} onClick={() => handleMainAnalytics(row.Id, row.DisplayName)}>
                      <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <IconBox active={isActive ? 1 : 0}>
                            <Iconify icon={row.Icon || 'mdi:head-snowflake-outline'} width={24} height={24} />
                          </IconBox>
                          {isActive && (
                            <Chip label="ON" size="small" color="primary"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, borderRadius: 1, px: 0.2 }}
                            />
                          )}
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.3, lineHeight: 1.3 }}>
                          {row.DisplayName}
                        </Typography>
                        <Typography variant="caption" color={isActive ? 'primary.light' : 'text.disabled'}>
                          {isActive && subCount > 0
                            ? `${activeSubCount} of ${subCount} enabled`
                            : subCount > 0
                            ? `${subCount} sub-model${subCount > 1 ? 's' : ''}`
                            : 'Click to configure'}
                        </Typography>
                      </Box>
                    </ModelCard>
                  </Grid>
                );
              })
            }

            {!loadingCards && filteredAnalyticsMain.length === 0 && cardSearch && (
              <Grid item xs={12}>
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.disabled" variant="body2">No models match "{cardSearch}"</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Sub-analytics dialog */}
        <Dialog
          open={activeAnalyticSub.isOpen}
          onClose={handleClose}
          PaperProps={{ sx: { borderRadius: 2, width: 400, maxWidth: '95vw', background: theme.palette.background.paper } }}
        >
          <DialogTitle sx={{ pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.15),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme.palette.primary.light,
              }}>
                <Iconify icon="mdi:tune-vertical" width={20} height={20} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {activeAnalyticSub.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sub-analytics configuration
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ px: 2, pt: 2, pb: 1 }}>
            {settingAlert && (
              <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 1.5, py: 0.5 }}>
                Please select at least one camera first.
              </Alert>
            )}
            {activeAnalyticSub.activeId && activeAnalyticSub.activeId.length > 0 ? (
              <FormGroup>
                {activeAnalyticSub.activeId.map((row, key) => {
                  const isOn = activeSubIDs.includes(row.Id);
                  return (
                    <SubRow key={key} active={isOn ? 1 : 0}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: isOn ? theme.palette.primary.main : theme.palette.grey[600],
                          flexShrink: 0, transition: 'background 0.2s',
                        }} />
                        <Typography variant="body2" fontWeight={isOn ? 600 : 400} color={isOn ? 'text.primary' : 'text.secondary'}>
                          {row.DisplayName}
                        </Typography>
                      </Box>
                      <Switch color="primary" size="small" checked={isOn} onChange={() => handleSwitchBar(row)} />
                    </SubRow>
                  );
                })}
              </FormGroup>
            ) : (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">No sub-analytics available.</Typography>
              </Box>
            )}
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              {activeAnalyticSub.activeId
                ? `${activeAnalyticSub.activeId.filter((r) => activeSubIDs.includes(r.Id)).length} of ${activeAnalyticSub.activeId.length} enabled`
                : ''}
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
              size="small"
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 2.5 }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast feedback */}
        <Snackbar
          open={savedResponse.status}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={3000}
          onClose={() => setSavedResponse({ status: false, title: null, isError: false })}
        >
          <Alert
            severity={savedResponse.isError ? 'error' : 'success'}
            variant="filled"
            sx={{ borderRadius: 2, minWidth: 260 }}
            onClose={() => setSavedResponse({ status: false, title: null, isError: false })}
          >
            {savedResponse.title}
          </Alert>
        </Snackbar>

        {/* bottom spacer so fixed bar doesn't cover last card */}
        <Box sx={{ pb: 7 }} />

        {/* Action bar — fixed to viewport bottom, left offset matches sidebar */}
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 'var(--sidebar-width, 280px)',
          right: 0,
          zIndex: theme.zIndex.appBar,
          py: 1.25,
          px: 2.5,
          background: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
            {activeCameras.length > 0 ? (
              <>
                <Chip
                  size="small"
                  label={`${activeCameras.length} camera${activeCameras.length > 1 ? 's' : ''}`}
                  color="primary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, borderRadius: 1 }}
                />
                {activeMainIDs.length > 0 && (
                  <Chip
                    size="small"
                    label={`${activeMainIDs.length} model${activeMainIDs.length !== 1 ? 's' : ''} active`}
                    color="success"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, borderRadius: 1 }}
                  />
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Select cameras to configure analytics
              </Typography>
            )}
          </Box>
          <LoadingButton
            variant="outlined"

            color="error"
            size="small"
            onClick={resetData}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
          >
            Reset
          </LoadingButton>
          <LoadingButton
            variant="contained"
            loading={isSaving}
            size="small"
            onClick={saveData}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 2.5 }}
          >
            Save Changes
          </LoadingButton>
        </Box>

      </Container>
    </Page>
  );
}
